import type React from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";
import { formatMetricValue, computePctChange } from "../../lib/format";
import { priorComparisonName } from "../../lib/explore-date-presets";
import { type MyBrandCardView } from "../../lib/metrics";
import { useTenantCurrency } from "@/lib/use-tenant-currency";

// ─── Types ────────────────────────────────────────────────────────────────────

export type MyBrandKpiCardsState = "loading" | "empty" | "ready" | "error";

interface MyBrandKpiCardsProps {
  state: MyBrandKpiCardsState;
  cards?: MyBrandCardView[];
  activePresetId: string;
  errorMessage?: string;
  selectedCards: Set<number>;
  onCardToggle: (index: number) => void;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const PLACEHOLDER_CLASS =
  "flex h-32 items-center justify-center rounded-xl border bg-card px-4 text-sm text-muted-foreground";

// ─── Component ───────────────────────────────────────────────────────────────

export function MyBrandKpiCards({
  state,
  cards,
  activePresetId,
  errorMessage,
  selectedCards,
  onCardToggle,
}: MyBrandKpiCardsProps) {
  const { t } = useTranslation();
  const { symbol: currencySymbol } = useTenantCurrency();

  if (state === "loading") {
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="flex flex-col gap-3 rounded-xl border bg-card p-4"
            style={{ "--shimmer-delay": `${i * 160}ms` } as React.CSSProperties}
          >
            <div className="skeleton-shimmer-bar h-3 w-20 rounded-md" />
            <div className="skeleton-shimmer-bar h-7 w-24 rounded-md" />
            <div className="skeleton-shimmer-bar h-3 w-16 rounded-md" />
          </div>
        ))}
      </div>
    );
  }

  if (state === "empty") {
    return (
      <div className={PLACEHOLDER_CLASS}>
        No data available for this period.
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className={PLACEHOLDER_CLASS}>
        {errorMessage ?? "Your data is being analyzed — something interesting is coming!"}
      </div>
    );
  }

  if (!cards) return null;

  const previousLabel = priorComparisonName(activePresetId, t);

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
      {cards.map((card) => {
        const pctChange = computePctChange(card.value, card.previousValue);
        const isPositive = pctChange >= 0;
        const TrendIcon = isPositive ? TrendingUp : TrendingDown;
        const isSelected = selectedCards.has(card.index);

        return (
          <div
            key={card.index}
            role="button"
            tabIndex={0}
            aria-pressed={isSelected}
            onClick={() => onCardToggle(card.index)}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                e.preventDefault();
                onCardToggle(card.index);
              }
            }}
            className={cn(
              "flex flex-col gap-2 rounded-xl border bg-card p-4 cursor-pointer select-none",
              "outline outline-2 outline-offset-2 [outline-color:var(--card-outline)]",
              "transition-[outline-color]",
            )}
            style={{
              "--card-outline": isSelected
                ? `var(${card.colorToken})`
                : `var(--border)`,
            } as React.CSSProperties}
          >
            <div className="flex items-center gap-1.5">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full transition-[background,box-shadow]"
                style={isSelected
                  ? { background: `var(${card.colorToken})` }
                  : { background: "transparent", boxShadow: `0 0 0 1.5px var(${card.colorToken})` }}
              />
              <span className="text-xs font-medium text-muted-foreground">
                {card.label}
              </span>
            </div>

            <span className="text-xl font-semibold tracking-tight text-foreground">
              {formatMetricValue(card.value, card.format, currencySymbol)}
            </span>

            <div
              className={`flex items-center gap-1 text-xs font-medium ${isPositive ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
            >
              <TrendIcon className="h-3 w-3 shrink-0" />
              <span>
                {t("dashboard.kpiCards.vsPriorWindow", "{{sign}}{{pct}}% vs. prior {{previous}}", {
                  sign: isPositive ? "+" : "",
                  pct: pctChange.toFixed(2),
                  previous: previousLabel,
                })}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
